import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  FaUser,
  FaWallet,
  FaUniversity,
  FaPhone,
  FaHashtag,
  FaIdCard,
  FaSave,
} from "react-icons/fa";

import {
  clearUpdateUserSuccess,
  getLogUserRequest,
  logout,
  updateUserRequest,
} from "../../redux/action";
import { RegisterButton } from "../Buttons"; // Assuming you have this
import { FormError } from "./FormError"; // Assuming you have this

// --- Reusable Input Component for Clean Code ---
const InputField = ({
  label,
  name,
  type = "text",
  formik,
  icon: Icon,
  placeholder,
}) => {
  const hasError = formik.touched[name] && formik.errors[name];

  return (
    <div className="w-full">
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon
              className={`text-gray-400 ${hasError ? "text-red-400" : ""}`}
            />
          </div>
        )}
        <input
          type={type}
          name={name}
          placeholder={placeholder}
          {...formik.getFieldProps(name)}
          className={`
            w-full pl-10 pr-4 py-3 rounded-lg border-2 transition-all outline-none
            ${
              hasError
                ? "border-red-300 bg-red-50 text-red-900 focus:border-red-500 placeholder-red-300"
                : " bg-gray-50 text-gray-700 focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10"
            }
          `}
        />
      </div>
      {hasError && (
        <div className="mt-1 text-xs text-red-500 font-medium flex items-center gap-1 animate-pulse">
          {formik.errors[name]}
        </div>
      )}
    </div>
  );
};

export const UpdateUserForm = () => {
  const dispatch = useDispatch();

  const { updateUserSuccess, updateUserError, updateUserLoading, user, token } =
    useSelector((state) => ({
      updateUserSuccess: state.ajaxStatuses.success.updateUser,
      updateUserError: state.ajaxStatuses.errors.updateUser,
      updateUserLoading: state.loadingIndicator.updateUserLoading,
      user: state.userData.user,
      token: state.userData.token,
    }));

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      phone: "",
      walletAddress: "",
      bankName: "",
      accountNumber: "",
      accountName: "",
    },
    validationSchema: yup.object({
      firstName: yup.string().required("First name is required"),
      lastName: yup.string().required("Last name is required"),
    }),
    onSubmit: (values) => {
      dispatch(updateUserRequest({ formData: values, id: user._id }));
    },
  });

  // --- Effects ---
  useEffect(() => {
    if (updateUserError) toast.error(updateUserError);
  }, [updateUserError]);

  useEffect(() => {
    if (updateUserSuccess) {
      toast.success("Profile updated! Logging out for security...");
      formik.resetForm();
      dispatch(clearUpdateUserSuccess());
      setTimeout(() => dispatch(logout()), 2000); // Give user time to read toast
    }
  }, [updateUserSuccess, dispatch, formik]);

  useEffect(() => {
    if (token) dispatch(getLogUserRequest(token));
  }, [dispatch, token]);

  useEffect(() => {
    if (user) {
      formik.setValues({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.phone || "",
        walletAddress: user.walletAddress || "",
        bankName: user.bankName || "",
        accountNumber: user.accountNumber || "",
        accountName: user.accountName || "",
      });
    }
  }, [user]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-sm border  overflow-hidden">
        {/* Header Section */}
        <div className="bg-gray-50/50 px-8 py-6 border-b  flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800 font-heading">
              Profile Settings
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Update your personal and financial information
            </p>
          </div>
          {/* Avatar Placeholder */}
          <div className="hidden md:flex h-12 w-12 rounded-full bg-primary-100 text-primary-600 items-center justify-center text-xl font-bold">
            {user?.firstName?.charAt(0) || "U"}
          </div>
        </div>

        <form onSubmit={formik.handleSubmit} className="p-6 md:p-8 space-y-8">
          {/* Section 1: Personal Identity */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 mb-5 flex items-center gap-2">
              <FaIdCard className="text-primary-500" /> Personal Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="First Name"
                name="firstName"
                placeholder="e.g. John"
                icon={FaUser}
                formik={formik}
              />
              <InputField
                label="Last Name"
                name="lastName"
                placeholder="e.g. Doe"
                icon={FaUser}
                formik={formik}
              />
              <div className="md:col-span-2">
                <InputField
                  label="Phone Number"
                  name="phone"
                  placeholder="+1 (555) 000-0000"
                  icon={FaPhone}
                  formik={formik}
                />
              </div>
            </div>
          </section>

          {/* Section 2: Financial Info */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 mb-5 flex items-center gap-2">
              <FaUniversity className="text-primary-500" /> Financial Settings
            </h3>

            <div className="space-y-6">
              <InputField
                label="Wallet Address (Crypto)"
                name="walletAddress"
                placeholder="0x..."
                icon={FaWallet}
                formik={formik}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <InputField
                    label="Bank Name"
                    name="bankName"
                    placeholder="e.g. Chase Bank"
                    icon={FaUniversity}
                    formik={formik}
                  />
                </div>
                <InputField
                  label="Account Number"
                  name="accountNumber"
                  placeholder="0000000000"
                  icon={FaHashtag}
                  formik={formik}
                />
                <InputField
                  label="Account Name"
                  name="accountName"
                  placeholder="e.g. John Doe"
                  icon={FaUser}
                  formik={formik}
                />
              </div>
            </div>
          </section>

          {/* Footer Action */}
          <div className="pt-4 flex items-center justify-end gap-4 border-t  mt-8">
            <p className="text-xs text-gray-400 hidden md:block">
              Changes will require a re-login for security.
            </p>
            <div className="w-full md:w-auto">
              <RegisterButton
                loading={updateUserLoading}
                disable={!(formik.dirty && formik.isValid)}
                action={formik.handleSubmit}
                text={"Save Changes"}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
